import React, { useState, useEffect, useRef } from 'react';
import { getGame, getGameBets, getGameBetStats, getUserBets } from '../../lib/bettingApi';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import BettingForm from './BettingForm';
import WinLossPopup from './WinLossPopup';
import ImageCropper from './ImageCropper';
import './GameDetail.css';

function GameDetail({ gameId, onBack, onLoginRequired }) {
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  const [bets, setBets] = useState([]);
  const [betStats, setBetStats] = useState({
    yes: { count: 0, total: 0 },
    no: { count: 0, total: 0 },
    teams: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [betsTab, setBetsTab] = useState('info'); // 'recent' or 'info' - default to 'info' (More tab)
  const [userBets, setUserBets] = useState([]);
  const [showWinLossPopup, setShowWinLossPopup] = useState(false);
  const [popupShown, setPopupShown] = useState(false);
  const [containerPaddingTop, setContainerPaddingTop] = useState(120); // Dynamic padding based on menu position
  const [creatorName, setCreatorName] = useState(null);
  const [selectedTeamNumber, setSelectedTeamNumber] = useState(null); // for multi-team PICK buttons
  const [showAddTeamModal, setShowAddTeamModal] = useState(false); // "+ Add Yours" modal
  const [addTeamImage, setAddTeamImage] = useState(''); // Image preview URL for new team
  const [addTeamImageFile, setAddTeamImageFile] = useState(null); // Image file for new team
  const [showAddTeamCropper, setShowAddTeamCropper] = useState(false); // Show image cropper for new team
  const [addTeamImageToCrop, setAddTeamImageToCrop] = useState(null); // Image to crop
  const [addTeamName, setAddTeamName] = useState(''); // Team name for new team
  const [addTeamColor, setAddTeamColor] = useState('#2551AF'); // Team color for new team
  const addTeamImageInputRef = useRef(null);

  // Calculate container padding based on menu position to maintain 10px gap
  useEffect(() => {
    const updateContainerPadding = () => {
      const header = document.querySelector('.user-header');
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const headerBottom = headerRect.bottom;
        const gap = 10; // 10px gap between menu and game windows
        setContainerPaddingTop(headerBottom + gap);
      }
    };

    // Initial calculation
    updateContainerPadding();

    // Update on resize
    window.addEventListener('resize', updateContainerPadding);
    
    // Use ResizeObserver to watch for header size changes
    const header = document.querySelector('.user-header');
    let resizeObserver;
    if (header && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateContainerPadding);
      resizeObserver.observe(header);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateContainerPadding);
      if (resizeObserver && header) {
        resizeObserver.unobserve(header);
      }
    };
  }, []);

  useEffect(() => {
    if (!gameId) return;

    // Reset popup state when switching games
    setShowWinLossPopup(false);
    setPopupShown(false);
    setUserBets([]);
    // Reset selected team when switching games; initial selection will be set after game loads
    setSelectedTeamNumber(null);

    fetchGame();
    fetchBets();
    fetchBetStats();
    
    // Subscribe to real-time updates
    const gameSubscription = subscribeToGame();
    const betsSubscription = subscribeToBets();
    
    // Cleanup subscriptions on unmount or gameId change
    return () => {
      if (gameSubscription) {
        gameSubscription.unsubscribe();
      }
      if (betsSubscription) {
        betsSubscription.unsubscribe();
      }
    };
  }, [gameId]);

  // Fetch user bets when user or gameId changes
  useEffect(() => {
    fetchUserBets();
    
    // Close popup if user logs out
    if (!user) {
      setShowWinLossPopup(false);
    }
  }, [user, gameId]);

  // Check localStorage on mount to see if popup was already shown
  useEffect(() => {
    if (game?.id) {
      const wasShown = localStorage.getItem(`popup_shown_${game.id}`) === 'true';
      setPopupShown(wasShown);
    }
  }, [game?.id]);

  // When a multi-team game loads, auto-select the first team shown in the list
  useEffect(() => {
    if (!game || !game.teams || !Array.isArray(game.teams)) return;
    if (selectedTeamNumber !== null) return; // don't override if user already picked

    // Only apply to true multi-team games (>2 teams)
    const hasMultipleTeams = game.teams.length > 2;
    if (!hasMultipleTeams) return;

    // Same filtering and sorting logic as in the render block so the first row matches here
    const teams = game.teams.filter(t => t.name || t.pool > 0);
    if (teams.length === 0) return;

    const totalPool = teams.reduce((sum, t) => sum + (parseFloat(t.pool) || 0), 0);
    const sortedTeams = [...teams].sort((a, b) => {
      const poolA = parseFloat(a.pool) || 0;
      const poolB = parseFloat(b.pool) || 0;
      const percentA = totalPool > 0 ? (poolA / totalPool) * 100 : 0;
      const percentB = totalPool > 0 ? (poolB / totalPool) * 100 : 0;
      return percentB - percentA; // highest first
    });

    const topTeam = sortedTeams[0];
    if (!topTeam) return;

    // Match the same key used in the button: team.team_number || index + 1
    // We need to find this team's index in the sorted array
    const topIndex = sortedTeams.indexOf(topTeam);
    const initialTeamNumber = topTeam.team_number || topIndex + 1;
    setSelectedTeamNumber(initialTeamNumber);
  }, [game, selectedTeamNumber]);

  // Detect game end and show popup (for initial load or manual status check)
  useEffect(() => {
    // Check if game just ended (for cases where game was already ended when component mounted)
    if (game?.status === 'ended' && game?.winner_side && user && !popupShown) {
      const checkAndShowPopup = async () => {
        try {
          // Fetch user bets directly
          const bets = await getUserBets(gameId);
          if (bets && bets.length > 0) {
            console.log('Game already ended on mount, showing popup:', bets.length);
            setUserBets(bets);
            setShowWinLossPopup(true);
            setPopupShown(true);
            localStorage.setItem(`popup_shown_${game.id}`, 'true');
          } else {
            console.log('Game ended but user has no bets');
          }
        } catch (err) {
          console.error('Error fetching user bets for popup:', err);
          // Don't show popup if we can't fetch bets
        }
      };
      
      // Small delay to ensure component is fully mounted
      const timeoutId = setTimeout(checkAndShowPopup, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [game?.status, game?.winner_side, game?.id, user, popupShown, gameId]);

  // Calculate and update time remaining
  useEffect(() => {
    if (!game || !game.ends_at || game.status !== 'active') {
      setTimeRemaining(null);
      return;
    }

    const calculateTimeRemaining = () => {
      const now = new Date();
      const endsAt = new Date(game.ends_at);
      const diff = endsAt - now;

      if (diff <= 0) {
        setTimeRemaining({ expired: true });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds, expired: false });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [game]);

  const fetchGame = async () => {
    try {
      const gameData = await getGame(gameId);
      setGame(gameData);
      setError('');
      
      // Fetch creator profile if created_by exists
      if (gameData.created_by) {
        try {
          const { data: profile, error: profileError } = await supabase
            .from('user_profiles_public')
            .select('nickname')
            .eq('id', gameData.created_by)
            .single();
          
          if (!profileError && profile) {
            setCreatorName(profile.nickname);
          }
        } catch (err) {
          console.error('Error fetching creator profile:', err);
        }
      }
      
    } catch (err) {
      console.error('Error fetching game:', err);
      setError('Failed to load game');
    } finally {
      setLoading(false);
    }
  };


  const fetchBets = async () => {
    try {
      const betsData = await getGameBets(gameId, 20);
      setBets(betsData || []);
    } catch (err) {
      console.error('Error fetching bets:', err);
      // Set empty array on error so UI still renders
      setBets([]);
    }
  };

  const fetchBetStats = async () => {
    try {
      const stats = await getGameBetStats(gameId);
      setBetStats(stats);
    } catch (err) {
      console.error('Error fetching bet statistics:', err);
    }
  };

  const fetchUserBets = async () => {
    if (!user || !gameId) {
      setUserBets([]);
      return;
    }

    try {
      const bets = await getUserBets(gameId);
      setUserBets(bets || []);
    } catch (err) {
      console.error('Error fetching user bets:', err);
      setUserBets([]);
    }
  };

  const subscribeToGame = () => {
    if (!gameId) {
      console.warn('Cannot subscribe to game: gameId is missing');
      return null;
    }

    const channel = supabase
      .channel(`game-${gameId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'games',
          filter: `id=eq.${gameId}`
        },
        async (payload) => {
          try {
            console.log('Game updated:', payload);
            // Update game with new pools and recalculate coefficients
            if (!payload.new) {
              console.warn('Game update payload missing new data');
              return;
            }
            // Support both new format (pool_1, pool_2) and old format (pool_yes, pool_no)
            const pool1 = parseFloat(payload.new.pool_1) || parseFloat(payload.new.pool_yes) || 0;
            const pool2 = parseFloat(payload.new.pool_2) || parseFloat(payload.new.pool_no) || 0;
            const poolYes = pool1;
            const poolNo = pool2;
            const totalPool = poolYes + poolNo;
            const defaultBet = 1;

            const coefficientYes = totalPool > 0 ? (totalPool + defaultBet) / (poolYes + defaultBet) : 1;
            const coefficientNo = totalPool > 0 ? (totalPool + defaultBet) / (poolNo + defaultBet) : 1;

            const updatedGame = {
              ...payload.new,
              pool_yes: poolYes,
              pool_no: poolNo,
              coefficient_yes: parseFloat(coefficientYes.toFixed(4)),
              coefficient_no: parseFloat(coefficientNo.toFixed(4))
            };

            const prevStatus = payload.old?.status;
            const newStatus = payload.new.status;
            
            setGame(prev => ({
              ...prev,
              ...updatedGame
            }));

            // If game status changed to 'ended', check and show popup
            if (newStatus === 'ended' && payload.new.winner_side && prevStatus !== 'ended') {
              console.log('Game ended detected via real-time update:', {
                gameId,
                winnerSide: payload.new.winner_side,
                prevStatus,
                newStatus
              });
              
              // Check if popup was already shown
              const wasShown = localStorage.getItem(`popup_shown_${gameId}`) === 'true';
              
              if (!wasShown && user) {
                // Fetch user bets and show popup if user has bets
                getUserBets(gameId)
                  .then(bets => {
                    if (bets && bets.length > 0) {
                      console.log('User has bets, showing popup:', bets.length);
                      setUserBets(bets);
                      setShowWinLossPopup(true);
                      setPopupShown(true);
                      localStorage.setItem(`popup_shown_${gameId}`, 'true');
                    } else {
                      console.log('User has no bets on this game, not showing popup');
                    }
                  })
                  .catch(err => {
                    console.error('Error fetching user bets for popup:', err);
                    // Don't show popup if we can't fetch bets
                  });
              } else if (wasShown) {
                console.log('Popup already shown for this game');
              } else if (!user) {
                console.log('User not logged in, not showing popup');
              }
            }
          } catch (error) {
            console.error('Error handling game update:', error);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Error subscribing to game updates:', gameId);
        }
      });

    return channel;
  };

  const subscribeToBets = () => {
    const channel = supabase
      .channel(`bets-${gameId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bets',
          filter: `game_id=eq.${gameId}`
        },
        async (payload) => {
          console.log('New bet:', payload);
          // Fetch the bet with user profile
          try {
            const newBet = payload.new;
            // Get user profile for the new bet using public view (accessible to non-logged-in users)
            const { data: profile } = await supabase
              .from('user_profiles_public')
              .select('id, nickname')
              .eq('id', newBet.user_id)
              .single();

            const betWithProfile = {
              ...newBet,
              user_profiles: {
                nickname: profile?.nickname || 'Anonymous'
              }
            };

            // Add new bet to the list
            setBets(prev => [betWithProfile, ...prev].slice(0, 20));
            
            // Update bet statistics
            fetchBetStats();
          } catch (err) {
            console.error('Error fetching profile for new bet:', err);
            // Still add the bet without profile
            const betWithProfile = {
              ...payload.new,
              user_profiles: {
                nickname: 'Anonymous'
              }
            };
            setBets(prev => [betWithProfile, ...prev].slice(0, 20));
            
            // Update bet statistics
            fetchBetStats();
          }
        }
      )
      .subscribe();

    return channel;
  };

  const handleBetPlaced = (result) => {
    // Refresh game and bets after bet is placed
    fetchGame();
    fetchBets();
    fetchBetStats();
  };

  // Handle file selection for "Add Yours" modal
  const handleAddTeamFileSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setAddTeamImageToCrop(imageUrl);
      setShowAddTeamCropper(true);
    };
    reader.readAsDataURL(file);
  };

  // Handle cropped image for "Add Yours" modal
  const handleAddTeamCropComplete = async (croppedImageUrl) => {
    // Revoke old blob URL if exists
    if (addTeamImage && addTeamImage.startsWith('blob:')) {
      URL.revokeObjectURL(addTeamImage);
    }
    setAddTeamImage(croppedImageUrl);
    
    // Convert blob URL to File if needed
    try {
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], `add-team-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setAddTeamImageFile(file);
    } catch (error) {
      console.error('Error converting cropped image to file:', error);
    }
    
    setShowAddTeamCropper(false);
    setAddTeamImageToCrop(null);
  };

  // Reset "Add Yours" modal state
  const resetAddTeamModal = () => {
    if (addTeamImage && addTeamImage.startsWith('blob:')) {
      URL.revokeObjectURL(addTeamImage);
    }
    setAddTeamImage('');
    setAddTeamImageFile(null);
    setShowAddTeamCropper(false);
    setAddTeamImageToCrop(null);
    setAddTeamName('');
    setAddTeamColor('#2551AF');
    setShowAddTeamModal(false);
  };

  // Handle "Add Yours" form submission
  const handleAddTeamSubmit = () => {
    // Check if user is logged in
    if (!user) {
      // Close the modal first
      resetAddTeamModal();
      // Then show login page
      if (onLoginRequired) {
        onLoginRequired();
      }
      return;
    }

    // TODO: Implement actual team submission logic here
    // For now, just close the modal
    console.log('Submitting team:', {
      name: addTeamName,
      color: addTeamColor,
      image: addTeamImageFile
    });
    
    // Close modal after submission
    resetAddTeamModal();
  };

  // Format simplified timer showing only the largest remaining unit
  const formatSimplifiedTimer = (timeRemaining) => {
    if (!timeRemaining || timeRemaining.expired) return null;
    
    if (timeRemaining.days > 0) {
      return `${timeRemaining.days} ${timeRemaining.days === 1 ? 'day' : 'days'} left`;
    } else if (timeRemaining.hours > 0) {
      return `${timeRemaining.hours} ${timeRemaining.hours === 1 ? 'hour' : 'hours'} left`;
    } else if (timeRemaining.minutes > 0) {
      return `${timeRemaining.minutes} ${timeRemaining.minutes === 1 ? 'minute' : 'minutes'} left`;
    } else {
      return `${timeRemaining.seconds} ${timeRemaining.seconds === 1 ? 'second' : 'seconds'} left`;
    }
  };


  if (loading) {
    return (
      <div className="game-detail-container" style={{ paddingTop: `${containerPaddingTop}px` }}>
        <div className="game-loading">Loading game...</div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="game-detail-container" style={{ paddingTop: `${containerPaddingTop}px` }}>
        <div className="game-error">{error || 'Game not found'}</div>
        {onBack && (
          <button onClick={onBack} className="back-btn">Back to Games</button>
        )}
      </div>
    );
  }

  // Get team colors from game data, fallback to defaults
  const teamYesColor = game?.team_yes_color || '#2551AF';
  const teamNoColor = game?.team_no_color || '#E23838';

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className="game-detail-container" style={{ paddingTop: `${containerPaddingTop}px` }}>

      <div className="game-detail-main-layout">
        <div className="left-column">
          <div className="info-section">
            <div className="title-row">
              <div className="game-detail-title-wrapper">
                {game.title_image && (
                  <img
                    src={game.title_image}
                    alt={game.title}
                    className="game-detail-title-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <h1 className="game-detail-title">{game.title}</h1>
              </div>
              <img 
                src="/images/mark.png" 
                alt="Mark" 
                className="mark-icon"
                onMouseEnter={(e) => e.target.src = "/images/markfill.png"}
                onMouseLeave={(e) => e.target.src = "/images/mark.png"}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!user && onLoginRequired) {
                    onLoginRequired();
                  }
                }}
              />
            </div>
            <div className="title-meta-info">
              <div className="coeff-volume">
                ${(() => {
                  // Calculate volume from teams array if available, otherwise use pool_yes/pool_no
                  if (game.teams && Array.isArray(game.teams)) {
                    const totalVolume = game.teams.reduce((sum, t) => sum + (parseFloat(t.pool) || 0), 0);
                    return Math.floor(totalVolume).toLocaleString();
                  }
                  const poolYes = parseFloat(game.pool_yes) || 0;
                  const poolNo = parseFloat(game.pool_no) || 0;
                  const totalVolume = poolYes + poolNo;
                  return Math.floor(totalVolume).toLocaleString();
                })()} Vol.
              </div>
              {game.status === 'active' && game.ends_at && timeRemaining && !timeRemaining.expired && formatSimplifiedTimer(timeRemaining) && (
                <div className="simplified-timer">
                  <img src="/images/clock.png" alt="Clock" className="clock-icon" />
                  <span className="timer-text">{formatSimplifiedTimer(timeRemaining)}</span>
                </div>
              )}
            </div>
            <div className="game-pools-display">
              {(() => {
                // Check if we have multiple teams (>2)
                const hasMultipleTeams = game.teams && Array.isArray(game.teams) && game.teams.length > 2;
                
                if (hasMultipleTeams) {
                  // Multi-team layout (3+ teams)
                  const teams = game.teams.filter(t => t.name || t.pool > 0);
                  const totalPool = teams.reduce((sum, t) => sum + (parseFloat(t.pool) || 0), 0);
                  
                  // Sort teams by percentage (highest to lowest)
                  const sortedTeams = [...teams].sort((a, b) => {
                    const poolA = parseFloat(a.pool) || 0;
                    const poolB = parseFloat(b.pool) || 0;
                    const percentA = totalPool > 0 ? (poolA / totalPool) * 100 : 0;
                    const percentB = totalPool > 0 ? (poolB / totalPool) * 100 : 0;
                    return percentB - percentA; // Sort descending (highest first)
                  });
                  
                  return (
                    <>
                      <div className="multi-team-container">
                        {/* Header row */}
                        <div className="multi-team-header-row">
                          <div className="multi-team-header multi-team-header-participant">Participants</div>
                          <div className="multi-team-header multi-team-header-odds">% Odds</div>
                          <div className="multi-team-header multi-team-header-button"></div>
                        </div>

                        {sortedTeams.map((team, index) => {
                          const pool = parseFloat(team.pool) || 0;
                          const percent = totalPool > 0 ? (pool / totalPool) * 100 : 0;
                          // Use team's color or fallback to default colors
                          const defaultColors = ['#2551AF', '#E23838', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899'];
                          const teamColor = team.color || defaultColors[index % defaultColors.length];
                          
                          return (
                            <div key={team.team_number || index} className="multi-team-row">
                              {/* Column 1: Participants (square + name) */}
                              <div className="multi-team-col multi-team-participant">
                                <div className="multi-team-image-wrapper">
                                  {team.image ? (
                                    <img 
                                      src={team.image} 
                                      alt={team.name || `Team ${index + 1}`} 
                                      className="multi-team-image"
                                      onError={(e) => { 
                                        e.target.style.display = 'none';
                                        const colorSquare = e.target.nextElementSibling;
                                        if (colorSquare) colorSquare.style.display = 'block';
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className="multi-team-color-square"
                                    style={{ 
                                      display: team.image ? 'none' : 'block',
                                      backgroundColor: teamColor
                                    }}
                                  ></div>
                                </div>
                                <div className="multi-team-name-wrapper">
                                  <div className="multi-team-name">
                                    {team.name || `Team ${index + 1}`}
                                  </div>
                                  <div 
                                    className="multi-team-name-bar"
                                    style={{ 
                                      backgroundColor: teamColor,
                                      opacity: 0.4,
                                      width: `${percent}%`
                                    }}
                                  ></div>
                                </div>
                              </div>

                              {/* Column 2: % Odds (percent only) */}
                              <div className="multi-team-col multi-team-odds">
                                <span className="multi-team-percent">
                                  {percent.toFixed(0)}%
                                </span>
                              </div>

                              {/* Column 3: Button with VOTE label only */}
                              <div className="multi-team-col multi-team-button">
                                <button
                                  type="button"
                                  className={`multi-team-pick-btn ${selectedTeamNumber === (team.team_number || index + 1) ? 'selected' : ''}`}
                                  disabled={game.status !== 'active'}
                                  onClick={() => setSelectedTeamNumber(team.team_number || index + 1)}
                                >
                                  Vote
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* "+ Add Yours" button - only for multi-team votes */}
                      <div className="add-yours-container">
                        <button
                          type="button"
                          className="add-yours-btn"
                          disabled={game.status !== 'active'}
                          onClick={() => setShowAddTeamModal(true)}
                        >
                          + Add Yours
                        </button>
                      </div>
                    </>
                  );
                }
                
                // Standard 2-team layout
                const coefYes = typeof game.coefficient_yes === 'number' ? game.coefficient_yes : 1;
                const coefNo = typeof game.coefficient_no === 'number' ? game.coefficient_no : 1;
                const totalCoef = coefYes + coefNo;
                // Reverse-mapped percentages: Yes gets No's share, No gets Yes's share
                const yesPercent = totalCoef > 0 ? (coefNo / totalCoef) * 100 : 50;
                const noPercent = totalCoef > 0 ? (coefYes / totalCoef) * 100 : 50;
                const poolYes = parseFloat(game.pool_yes) || 0;
                const poolNo = parseFloat(game.pool_no) || 0;
                const totalVolume = poolYes + poolNo;

                return (
                  <div className="coeff-vertical-container">
                    <div className="coeff-vertical">
                      <div className="coeff-square-wrapper">
                        {game.team_yes_image ? (
                          <img 
                            src={game.team_yes_image} 
                            alt={game.team_yes_name || 'Yes'} 
                            className="team-image team-image-yes"
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              // Show colored square as fallback
                              const colorSquare = e.target.nextElementSibling;
                              if (colorSquare) colorSquare.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <div 
                          className="coeff-color-square coeff-color-square-yes" 
                          style={{ 
                            display: game.team_yes_image ? 'none' : 'block',
                            backgroundColor: teamYesColor
                          }}
                        ></div>
                        <div className="coeff-label-container">
                          <div className="coeff-name coeff-name-yes">
                            {game.team_yes_name || 'Yes'}
                          </div>
                        </div>
                      </div>
                      <div className="coeff-bar-wrapper">
                        <div className="coeff-bar-vertical-wrapper">
                          <span className="coeff-percent-text coeff-percent-yes">
                            {yesPercent.toFixed(0)}%
                          </span>
                          <div
                            className="coeff-bar-vertical coeff-bar-yes"
                            style={{ 
                              height: `${yesPercent}%`,
                              backgroundColor: teamYesColor
                            }}
                          >
                          </div>
                        </div>
                      </div>
                      <div className="coeff-vs-center">vs</div>
                      <div className="coeff-bar-wrapper">
                        <div className="coeff-bar-vertical-wrapper">
                          <span className="coeff-percent-text coeff-percent-no">
                            {noPercent.toFixed(0)}%
                          </span>
                          <div
                            className="coeff-bar-vertical coeff-bar-no"
                            style={{ 
                              height: `${noPercent}%`,
                              backgroundColor: teamNoColor
                            }}
                          >
                          </div>
                        </div>
                      </div>
                      <div className="coeff-square-wrapper">
                        {game.team_no_image ? (
                          <img 
                            src={game.team_no_image} 
                            alt={game.team_no_name || 'No'} 
                            className="team-image team-image-no"
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              // Show colored square as fallback
                              const colorSquare = e.target.nextElementSibling;
                              if (colorSquare) colorSquare.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <div 
                          className="coeff-color-square coeff-color-square-no" 
                          style={{ 
                            display: game.team_no_image ? 'none' : 'block',
                            backgroundColor: teamNoColor
                          }}
                        ></div>
                        <div className="coeff-label-container">
                          <div className="coeff-name coeff-name-no">
                            {game.team_no_name || 'No'}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Winner Display */}
                    {game.status === 'ended' && game.winner_side && (
                      <div className="winner-display">
                        <div className="winner-banner">
                          <span className="winner-label">Winner:</span>
                          <span className={`winner-team ${game.winner_side === 'yes' ? 'winner-yes' : 'winner-no'}`}>
                            {game.winner_side === 'yes' 
                              ? (game.team_yes_name || 'Yes')
                              : (game.team_no_name || 'No')
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="bets-section">
            <div className="bets-section-tabs">
              <span 
                className={`bets-tab ${betsTab === 'info' ? 'active' : ''}`}
                onClick={() => setBetsTab('info')}
              >
                More
              </span>
              <span 
                className={`bets-tab ${betsTab === 'recent' ? 'active' : ''}`}
                onClick={() => setBetsTab('recent')}
              >
                Trades
              </span>
            </div>
            
            {betsTab === 'recent' && (
              <>
                {bets.length === 0 ? (
                  <div className="no-bets">No bets yet. Be the first to bet!</div>
                ) : (
                  <div className="bets-list">
                    {bets.map((bet) => (
                      <div key={bet.id} className="bet-item">
                        <div className="bet-header">
                          <span className="bet-user">
                            {bet.user_profiles?.nickname || 'Anonymous'}
                          </span>
                          <span 
                            className={`bet-side ${bet.side || (bet.team_number === 1 ? 'yes' : 'no')}`}
                            style={{ 
                              color: (bet.side === 'yes' || bet.team_number === 1) ? teamYesColor : teamNoColor,
                              background: (bet.side === 'yes' || bet.team_number === 1)
                                ? hexToRgba(teamYesColor, 0.2) 
                                : hexToRgba(teamNoColor, 0.2),
                              borderColor: (bet.side === 'yes' || bet.team_number === 1)
                                ? hexToRgba(teamYesColor, 0.3) 
                                : hexToRgba(teamNoColor, 0.3)
                            }}
                          >
                            {(bet.side === 'yes' || bet.team_number === 1)
                              ? (game?.team_yes_name || 'Yes')
                              : (game?.team_no_name || 'No')
                            }
                          </span>
                        </div>
                        <div className="bet-details">
                          <span>Bet: ${parseFloat(bet.amount).toLocaleString()}</span>
                          <span>Payout: ${parseFloat(bet.payout).toLocaleString()}</span>
                          <span>Coef: {parseFloat(bet.coefficient).toFixed(4)}</span>
                        </div>
                        <div className="bet-time">
                          {new Date(bet.created_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {betsTab === 'info' && (
              <div className="bets-info-content">
                {game.description && (
                  <div className="game-description-section">
                    <div className="game-description-label">Description</div>
                    <p className="game-description-text">{game.description}</p>
                  </div>
                )}
                <div className="bet-stats-container">
                  <div className="bet-stats-header-row">
                    <div className="bet-stats-header bet-stats-col-name">Name</div>
                    <div className="bet-stats-header bet-stats-col-pool">Pool</div>
                    <div className="bet-stats-header bet-stats-col-bets">Votes</div>
                    <div className="bet-stats-header bet-stats-col-total">Total</div>
                  </div>

                  {Array.isArray(game.teams) && game.teams.length > 0 ? (
                    game.teams.map((team) => {
                      const teamNum = team.team_number;
                      const teamStats =
                        (betStats.teams && betStats.teams[teamNum]) || { count: 0, total: 0 };
                      const pool = Math.floor(parseFloat(team.pool) || 0);

                      return (
                        <div key={teamNum} className="bet-stats-row">
                          <div className="bet-stats-cell bet-stats-col-name">
                            <span className="bet-stat-name">
                              {team.name || `Team ${teamNum}`}
                            </span>
                          </div>
                          <div className="bet-stats-cell bet-stats-col-pool">
                            <span className="bet-stat-pool">
                              ${pool.toLocaleString()}
                            </span>
                          </div>
                          <div className="bet-stats-cell bet-stats-col-bets">
                            <span className="bet-stat-count">
                              {teamStats.count}
                            </span>
                          </div>
                          <div className="bet-stats-cell bet-stats-col-total">
                            <span className="bet-stat-amount">
                              ${teamStats.total.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <>
                      <div className={`bet-stats-row bet-stat-yes ${game.status === 'ended' && game.winner_side === 'yes' ? 'winner-highlight' : ''}`}>
                        <div className="bet-stats-cell bet-stats-col-name">
                          <span className="bet-stat-name">
                            {game.team_yes_name || 'Yes'}
                          </span>
                          {game.status === 'ended' && game.winner_side === 'yes' && (
                            <span className="winner-badge">WINNER</span>
                          )}
                        </div>
                        <div className="bet-stats-cell bet-stats-col-pool">
                          <span className="bet-stat-pool">
                            ${Math.floor(parseFloat(game.pool_yes) || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="bet-stats-cell bet-stats-col-bets">
                          <span className="bet-stat-count">
                            {betStats.yes.count}
                          </span>
                        </div>
                        <div className="bet-stats-cell bet-stats-col-total">
                          <span className="bet-stat-amount">
                            ${betStats.yes.total.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className={`bet-stats-row bet-stat-no ${game.status === 'ended' && game.winner_side === 'no' ? 'winner-highlight' : ''}`}>
                        <div className="bet-stats-cell bet-stats-col-name">
                          <span className="bet-stat-name">
                            {game.team_no_name || 'No'}
                          </span>
                          {game.status === 'ended' && game.winner_side === 'no' && (
                            <span className="winner-badge">WINNER</span>
                          )}
                        </div>
                        <div className="bet-stats-cell bet-stats-col-pool">
                          <span className="bet-stat-pool">
                            ${Math.floor(parseFloat(game.pool_no) || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="bet-stats-cell bet-stats-col-bets">
                          <span className="bet-stat-count">
                            {betStats.no.count}
                          </span>
                        </div>
                        <div className="bet-stats-cell bet-stats-col-total">
                          <span className="bet-stat-amount">
                            ${betStats.no.total.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="betting-section">
          <BettingForm
            game={game}
            onBetPlaced={handleBetPlaced}
            onLoginRequired={onLoginRequired}
            selectedTeamNumber={selectedTeamNumber}
          />
        </div>
      </div>

      {/* "+ Add Yours" Modal */}
      {showAddTeamModal && (
        <div className="auth-overlay">
          <div className="auth-modal">
            <button
              type="button"
              className="close-auth-btn"
              onClick={resetAddTeamModal}
            >
              ×
            </button>
            <div className="auth-container">
              <div className="auth-card add-yours-card">
                <h2 className="auth-title">Add Yours</h2>
                <div className="add-yours-form">
                  <div className="add-yours-row">
                    <div className="add-yours-image-square">
                      {addTeamImage ? (
                        <img
                          src={addTeamImage}
                          alt="Team preview"
                          className="add-yours-image-preview"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : null}
                      <input
                        type="file"
                        ref={addTeamImageInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleAddTeamFileSelect(e.target.files[0]);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="upload-image-btn"
                        onClick={() => addTeamImageInputRef.current?.click()}
                      >
                        {addTeamImage ? 'Change Image' : 'Upload Image'}
                      </button>
                    </div>
                  </div>
                  <div className="add-yours-row">
                    <label className="add-yours-label">Team name</label>
                    <input
                      type="text"
                      className="auth-input"
                      placeholder="Enter team name"
                      value={addTeamName}
                      onChange={(e) => setAddTeamName(e.target.value)}
                    />
                  </div>
                  <div className="add-yours-row">
                    <label className="add-yours-label">Color</label>
                    <div className="add-yours-color-row">
                      <input
                        type="color"
                        className="add-yours-color-input"
                        value={addTeamColor}
                        onChange={(e) => setAddTeamColor(e.target.value)}
                      />
                      <input
                        type="text"
                        className="auth-input add-yours-color-hex"
                        placeholder="#2551AF"
                        value={addTeamColor}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                            setAddTeamColor(value || '#2551AF');
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (!/^#[0-9A-Fa-f]{6}$/.test(value)) {
                            setAddTeamColor('#2551AF');
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="add-yours-actions">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={resetAddTeamModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="auth-submit-btn"
                      onClick={handleAddTeamSubmit}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Cropper Modal for "Add Yours" */}
      {showAddTeamCropper && addTeamImageToCrop && (
        <ImageCropper
          image={addTeamImageToCrop}
          onCropComplete={handleAddTeamCropComplete}
          onClose={() => {
            setShowAddTeamCropper(false);
            setAddTeamImageToCrop(null);
          }}
          aspectRatio={1}
        />
      )}

      {/* Win/Loss Popup */}
      <WinLossPopup
        game={game}
        userBets={userBets}
        isOpen={showWinLossPopup}
        onClose={() => setShowWinLossPopup(false)}
      />
    </div>
  );
}

export default GameDetail;

